import React, { Suspense, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import type { Group } from 'three';
import { FrameVehicle } from '../../hooks/useSimulationFrames';
import { useAppSelector } from '../../hooks/redux';
import {
  HOIST_SETTLE_SEC,
  OhtVehicle3DModel,
} from './OhtVehicle3DModel';
import {
  getPlaybackPosition,
  isVehicleStoppedAt,
  shouldPinOhtToTransferDock,
  shouldPlayOhtHoistAtDock,
  type LoadUnloadAnimation,
} from '../../utils/simulationPlayback';
import { simulationClockStore } from '../../store/simulationClockStore';
import { replaySnapshotStore } from '../../store/replaySnapshotStore';
import { railTransferPoseStore } from '../../store/railTransferPoseStore';
import { playbackDelayStore } from '../../store/playbackDelayStore';
import { transferWallHoldStore } from '../../store/transferWallHoldStore';
import { traceOhtPlaybackPosition } from '../../utils/ohtPlaybackDebug';
import { getOhtDockPosition } from '../../utils/topology';
import {
  findActiveApiLeg,
  ohtTip,
  phaseChipClass,
  phaseChipDisplayLabel,
  shortEndpointLabel,
} from '../../utils/simulationPhaseUi';
import type { OhtTipChip, SimulationPhaseChip } from '../../utils/simulationPhaseUi';
import { GATE_HANDOFF_DECK_Y } from '../../utils/derivedGateEquipment';
import type { FoupKind } from './amhs/types';
import { resolveOhtCarriedFoupVisibleFromRuntime } from '../../features/simulation/ohtCarriedFoupVisible';
import type { AmhsFinalSimulationLeg } from '../../types/simulationPlayback';
import {
  resolveTransferStation,
} from './amhs/Equipment/stationPortIndex';
import { resolveStationHandoffDeckY } from '../../utils/ohtHoist';
import { liveOhtRuntimeStore } from '../../store/liveOhtRuntimeStore';
import {
  SimOverlaySharedProvider,
  useSimOverlayShared,
} from './SimOverlaySharedContext';

/**
 * A port with no rendered station leaves the hoist retracted, which is silent on
 * screen. Report it once per port so the mismatch is diagnosable without
 * flooding the console from the frame loop.
 */
const unresolvedTransferStations = new Set<string>();
const EMPTY_API_LEGS: AmhsFinalSimulationLeg[] = [];
function warnUnresolvedTransferStation(
  ohtId: string,
  portId: string | undefined
): void {
  const key = portId ?? '(no portId)';
  if (unresolvedTransferStations.has(key)) return;
  unresolvedTransferStations.add(key);
  console.warn(
    `[TRANSFER_STATION_UNRESOLVED] ${ohtId} is holding at "${key}" but no rendered station owns that port; the hoist stays retracted.`
  );
}

/** Seconds the dwell occupies; 1 keeps older plans on the normalised track. */
function transferWindowS(transfer: { durationS?: number }): number {
  const seconds = transfer.durationS;
  return typeof seconds === 'number' && seconds > 0 ? seconds : 1;
}

const SimulatedVehicleMesh = React.memo(function SimulatedVehicleMesh({ v }: { v: FrameVehicle }) {
  const groupRef = useRef<Group>(null);
  const showOhtRouteLabels = useAppSelector(
    (state) => state.ui.showOhtRouteLabels ?? true
  );
  const {
    simPaused,
    source,
    apiLegsByOhtRef,
    stationIndex,
    gatePointByPortId,
    playbackVehiclesRef,
    idleByVehicleRef,
    endpointNames,
    requestStationsById,
  } = useSimOverlayShared();
  const elementByPortId = stationIndex.byPortId;


  // Only transfer/load flags live in React state — pose is imperative (avoids
  // re-rendering the GLB every frame while OHTs move).
  const [anim, setAnim] = useState<
    LoadUnloadAnimation & {
      isLoaded: boolean;
      foupKind: FoupKind;
      status: SimulationPhaseChip;
      statusLabel: string;
      tipChip?: OhtTipChip;
      transferDeckY?: number;
      hoistSettleS: number;
    }
  >(() => ({
    isPaused: v.isPaused ?? false,
    stopElapsedS: v.stopElapsedS,
    stopDurationS: v.stopDurationS,
    stopLoadedAction: v.stopLoadedAction,
    transferPortId: v.transferPortId,
    isLoaded: v.isLoaded ?? false,
    foupKind: 'raw',
    status: v.phaseChip ?? 'idle',
    statusLabel: phaseChipDisplayLabel(v.phaseChip ?? 'idle'),
    tipChip: undefined,
    hoistSettleS: HOIST_SETTLE_SEC,
  }));

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;

    const simT = simulationClockStore.getTime();
    const playback = playbackVehiclesRef.current[v.ohtId];
    const idle = idleByVehicleRef.current[v.ohtId];
    // Match 2D / Canvas3D: junction waits rewind this OHT's clock so it resumes
    // at the hub instead of teleporting ahead on the undelayed timeline.
    const runtime =
      source === 'live' ? liveOhtRuntimeStore.getOht(v.ohtId) : null;
    const t =
      runtime?.localSimTimeS ??
      playbackDelayStore.getEffectiveTime(v.ohtId, simT);
    let x = runtime?.x ?? v.x;
    let y = runtime?.y ?? v.y;
    let angle = runtime?.angle ?? v.angle;
    let scheduleMoving = false;

    if (runtime) {
      scheduleMoving = runtime.state === 'MOVING';
    } else if (playback && idle) {
      const position = getPlaybackPosition(playback, idle, t, v.idleAngle);
      x = position.x;
      y = position.y;
      angle = position.angle;
      scheduleMoving =
        position.leg != null &&
        (position.leg.fromX !== position.leg.toX ||
          position.leg.fromY !== position.leg.toY);
      traceOhtPlaybackPosition(v.ohtId, t, position);
    }

    const override = railTransferPoseStore.get(v.ohtId) ?? railTransferPoseStore.get(v.id);
    // A streamed equipment PICKUP/DROP freezes both pose and heading. A stale
    // turntable/connector override must not rotate the vehicle while its hoist
    // is operating at a tool or gate.
    if (override && !runtime?.transfer && !runtime?.gateTransfer) {
      x = override.x;
      y = override.y;
      angle = override.angle;
    }

    const replay = replaySnapshotStore.getSnapshot()?.ohts[v.ohtId];
    const activeTransfer =
      source === 'live' ? undefined : replay?.activeTransfer;
    const nowMs = performance.now();
    // Armed on arrival, started when this vehicle's clock reaches the handoff.
    const wallHold =
      source === 'live'
        ? null
        : transferWallHoldStore.get(v.ohtId, nowMs, t);
    // Freeze this vehicle's clock for the rest of the pin, so the route it
    // resumes is the one continuing from the port.
    if (source !== 'live') {
      transferWallHoldStore.pinTimeline(
        wallHold,
        simT,
        t,
        scheduleMoving
      );
      playbackDelayStore.settle(v.ohtId, simT, !wallHold && !scheduleMoving);
    }
    // `activeTransfer` is timed off the shared clock. While this vehicle runs
    // behind it, that transfer is one it has not reached, and pinning to its
    // port would teleport the vehicle forward down the route.
    const onSharedClock = playbackDelayStore.getDelaySec(v.ohtId) <= 0;
    const transferPortId =
      runtime?.transfer?.portId ?? wallHold?.portId ?? activeTransfer?.portId;
    const transferOwner = resolveTransferStation(
      stationIndex,
      transferPortId,
      runtime?.transfer?.stationId ?? activeTransfer?.stationId
    );
    const transferDock = transferOwner
      ? getOhtDockPosition(transferOwner, transferPortId)
      : null;
    const handoffX = wallHold?.x ?? activeTransfer?.handoffX;
    const handoffY = wallHold?.y ?? activeTransfer?.handoffY;
    const streamedHandoff =
      typeof handoffX === 'number' &&
      Number.isFinite(handoffX) &&
      typeof handoffY === 'number' &&
      Number.isFinite(handoffY)
        ? { x: handoffX, y: handoffY }
        : null;
    const gatePoint = transferPortId ? gatePointByPortId.get(transferPortId) : undefined;
    // The stream node is authoritative. A layout dock is only a fallback for
    // older events that do not publish transfer coordinates. A wall hold falls
    // back once more, to where the vehicle stood, so a stocker event with no
    // node and no resolvable dock still parks it for the whole hold.
    const knownPoint = streamedHandoff ?? transferDock ?? gatePoint ?? null;
    const transferPoint =
      runtime?.gateTransfer
        ? { x: runtime.x, y: runtime.y }
        : transferWallHoldStore.holdPoint(wallHold, knownPoint, { x, y }) ??
          knownPoint;
    const runtimeTransfer = runtime?.transfer;
    const isOhtTransfer =
      !!runtimeTransfer ||
      !!wallHold ||
      (onSharedClock &&
        !!activeTransfer &&
        (activeTransfer.kind === 'OHT_PICKUP' ||
          activeTransfer.kind === 'OHT_DROP' ||
          activeTransfer.kind === 'EQUIPMENT_TRANSFER') &&
        (activeTransfer.action === 'PICKUP' || activeTransfer.action === 'DROP') &&
        !!activeTransfer.ohtId);
    const replayAction =
      activeTransfer?.action === 'PICKUP' || activeTransfer?.action === 'DROP'
        ? activeTransfer.action
        : null;
    const transferAction =
      runtimeTransfer?.action ??
      wallHold?.action ??
      replayAction ??
      (runtime?.gateTransfer
        ? runtime.gateTransfer.role === 'exit'
          ? 'DROP'
          : 'PICKUP'
        : null);
    const transferDeckY = runtime?.gateTransfer
      ? GATE_HANDOFF_DECK_Y
      : resolveStationHandoffDeckY(transferOwner);
    if (isOhtTransfer && transferAction && transferDeckY === undefined) {
      warnUnresolvedTransferStation(v.ohtId, transferPortId);
    }

    // In standard fab AMHS, the OHT body remains parked on the overhead rail at
    // the stop node throughout the transfer rather than overriding (x, y) to
    // the specific load port dock position. The equipment hoist/gripper/lifter
    // travels down and aligns with the target load port while the vehicle stays on-track.
    const hoistAtDock =
      isOhtTransfer &&
      // Never lower toward the world origin when a backend port cannot be
      // matched to a rendered station. Keep the OHT pinned with the hoist
      // retracted until the physical handoff deck is known.
      transferDeckY !== undefined &&
      (transferPoint
        ? true
        : transferOwner
          ? shouldPlayOhtHoistAtDock(transferOwner.type, { x, y }, transferDock)
          : true);
    // If no streamed/layout point exists, require the kinetic timeline to have
    // stopped before operating the hoist.
    const stopped = transferPoint ? true : isVehicleStoppedAt(playback?.legs, t);
    const transfer: LoadUnloadAnimation =
      hoistAtDock &&
      stopped &&
      isOhtTransfer &&
      transferAction
        ? {
            isPaused: true,
            // Live handoffs follow the DES sim window. Replay still uses wall hold.
            stopElapsedS: runtimeTransfer
              ? (runtimeTransfer.elapsedSimS ?? runtimeTransfer.elapsedWallS)
              : wallHold
                ? Math.max(0, (nowMs - wallHold.startedAtMs) / 1000)
                : activeTransfer!.progress01 * transferWindowS(activeTransfer!),
            stopDurationS:
              runtimeTransfer?.durationSimS ??
              runtimeTransfer?.holdWallSec ??
              wallHold?.durationS ??
              transferWindowS(activeTransfer!),
            stopLoadedAction: transferAction === 'PICKUP',
            transferPortId,
            eventTime: replaySnapshotStore.getSnapshot()?.simTime,
            shouldExecuteTransfer: true,
          }
        : { isPaused: false };
    const isLoaded =
      source === 'live' && runtime
        ? resolveOhtCarriedFoupVisibleFromRuntime(runtime)
        : runtime?.gateTransfer
          ? runtime.gateTransfer.foupState === 'ON_OHT'
          : runtime?.carriedFoupId != null ||
            runtimeTransfer?.action === 'DROP' ||
            wallHold?.action === 'DROP' ||
            (source !== 'live' && replay?.loadedFoupId != null);
    // The scheduler classifies the carrier at pickup; an exit-gate handoff is
    // processed by definition and covers replays that carry no kind at all.
    const foupKind: FoupKind =
      runtime?.carriedFoupKind ??
      (runtime?.gateTransfer?.role === 'exit' ? 'processed' : 'raw');
    const activeLeg = findActiveApiLeg(
      apiLegsByOhtRef.current.get(v.ohtId) ?? EMPTY_API_LEGS,
      v.ohtId,
      simT
    );
    const stations = activeLeg?.requestId
      ? requestStationsById[activeLeg.requestId]
      : undefined;
    const pickupFrom =
      stations?.pickup ||
      shortEndpointLabel(activeLeg?.fromPortId, endpointNames);
    const dropTo =
      stations?.drop || shortEndpointLabel(activeLeg?.toPortId, endpointNames);
    const tip = ohtTip(activeLeg, {
      transferAction,
      loaded: isLoaded,
      pickupFrom,
      dropTo,
    });
    const status: SimulationPhaseChip =
      tip.chip === 'pick' || tip.chip === 'drop' || tip.chip === 'hold'
        ? transfer.transferPortId || tip.chip === 'hold'
          ? 'wait'
          : phaseChipClass(activeLeg)
        : phaseChipClass(activeLeg);
    const statusLabel = showOhtRouteLabels
      ? tip.label
      : phaseChipDisplayLabel(status);
    const tipChip: OhtTipChip | undefined =
      tip.chip === 'pick' || tip.chip === 'drop' || tip.chip === 'hold'
        ? tip.chip
        : undefined;
    const hoistSettleS = activeTransfer?.preSettled
      ? 0
      : HOIST_SETTLE_SEC;

    if (
      activeTransfer?.kind === 'OHT_ARRIVE' &&
      activeTransfer.portId
    ) {
      const owner = elementByPortId.get(activeTransfer.portId);
      const dock = owner ? getOhtDockPosition(owner, activeTransfer.portId) : null;
      if (shouldPinOhtToTransferDock(owner?.type, { x, y }, dock)) {
        const progress = Math.min(1, Math.max(0, activeTransfer.phaseProgress01));
        const eased = progress * progress * (3 - 2 * progress);
        x += (dock.x - x) * eased;
        y += (dock.y - y) * eased;
      }
    }

    if (replay?.holdReason) {
      const waitPort = replay.atPortId ? elementByPortId.get(replay.atPortId) : undefined;
      const waitDock = waitPort ? getOhtDockPosition(waitPort, replay.atPortId) : null;
      if (shouldPinOhtToTransferDock(waitPort?.type, { x, y }, waitDock)) {
        x = waitDock.x;
        y = waitDock.y;
      }
    }

    // The streamed hold belongs to the OHT for both PICKUP and DROP.

    group.position.set(x, v.groupY, y);
    group.rotation.set(0, -(angle * Math.PI) / 180, 0);

    const effectiveDrawBox = isLoaded;

    setAnim((prev) => {
      if (
        prev.isPaused === transfer.isPaused &&
        prev.stopLoadedAction === transfer.stopLoadedAction &&
        prev.stopDurationS === transfer.stopDurationS &&
        prev.transferPortId === transfer.transferPortId &&
        prev.transferDeckY === transferDeckY &&
        prev.hoistSettleS === hoistSettleS &&
        prev.isLoaded === effectiveDrawBox &&
        prev.foupKind === foupKind &&
        prev.status === status &&
        prev.statusLabel === statusLabel &&
        prev.tipChip === tipChip
      ) {
        return prev;
      }
      return {
        ...transfer,
        isLoaded: effectiveDrawBox,
        foupKind,
        status,
        statusLabel,
        tipChip,
        transferDeckY,
        hoistSettleS,
      };
    });
  });

  return (
    <group ref={groupRef}>
      <group position={[-v.pivotX, 0, -v.pivotY]}>
        <OhtVehicle3DModel
          w={v.w}
          h={v.h}
          depth={v.meshDepth}
          opacity={1}
          paused={simPaused}
          groupY={v.groupY}
          isPaused={anim.isPaused}
          stopElapsedS={anim.stopElapsedS}
          stopDurationS={anim.stopDurationS}
          stopLoadedAction={anim.stopLoadedAction}
          hoistSettleS={anim.hoistSettleS}
          transferDeckY={anim.transferDeckY}
          isLoaded={anim.isLoaded}
          foupKind={anim.foupKind}
          status={anim.status}
          tipChip={anim.tipChip}
          statusLabel={anim.statusLabel}
          showStatusBadge={
            showOhtRouteLabels &&
            (anim.status !== 'idle' || anim.tipChip !== undefined)
          }
          pivotX={v.pivotX}
          pivotY={v.pivotY}
        />
      </group>
    </group>
  );
});

function SimulatedVehicleFallback({ v }: { v: FrameVehicle }) {
  const groupRef = useRef<Group>(null);
  const { playbackVehiclesRef, idleByVehicleRef } = useSimOverlayShared();

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;

    const playback = playbackVehiclesRef.current[v.ohtId];
    const idle = idleByVehicleRef.current[v.ohtId];
    let x = v.x;
    let y = v.y;
    let angle = v.angle;
    if (playback && idle) {
      const t = playbackDelayStore.getEffectiveTime(
        v.ohtId,
        simulationClockStore.getTime()
      );
      const position = getPlaybackPosition(
        playback,
        idle,
        t,
        v.idleAngle
      );
      x = position.x;
      y = position.y;
      angle = position.angle;
    }

    const override = railTransferPoseStore.get(v.ohtId) ?? railTransferPoseStore.get(v.id);
    if (override) {
      x = override.x;
      y = override.y;
      angle = override.angle;
    }

    group.position.set(x, v.groupY, y);
    group.rotation.set(0, -(angle * Math.PI) / 180, 0);
  });

  return (
    <group ref={groupRef}>
      <group position={[-v.pivotX, 0, -v.pivotY]}>
        <mesh position={[v.w / 2, 0, v.h / 2]}>
          <boxGeometry args={[v.w, v.meshDepth, v.h]} />
          <meshBasicMaterial color={v.color} toneMapped={false} />
        </mesh>
      </group>
    </group>
  );
}

const SimulationOverlay3D: React.FC<{ frames: FrameVehicle[] }> = React.memo(({ frames: frame }) => {
  const running = useAppSelector((state) => state.simulation.running);

  if (!running || frame.length === 0) return null;

  return (
    <SimOverlaySharedProvider>
      {frame.map((v) => (
        <Suspense key={`sim-oht-3d-${v.ohtId}`} fallback={<SimulatedVehicleFallback v={v} />}>
          <SimulatedVehicleMesh v={v} />
        </Suspense>
      ))}
    </SimOverlaySharedProvider>
  );
});

export default SimulationOverlay3D;
